import { Building2, UserCircle, Target, DollarSign, AlertCircle, Users } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function CrmSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        The CRM is where your customer relationships live. Every company, person, prospect, and deal
        has a home here. Understanding how these records connect to each other is key to working
        efficiently — especially when you are juggling dozens (or hundreds) of deals at once.
      </p>

      <section id="accounts" aria-labelledby="accounts-heading">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-4 w-4" aria-hidden="true" />
          <h3 id="accounts-heading" className="font-semibold">
            Accounts
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          An account is a company or organization you do business with (or want to). It is the
          anchor record — contacts, opportunities, tasks, and notes all connect back to accounts.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">What you can track:</span> Company name, industry, annual
          revenue, website, phone, billing and shipping addresses, account type (Direct or
          Reseller), and owner.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Address autocomplete:</span> When entering billing or
          shipping addresses, start typing and select from Google Maps suggestions. The city, state,
          postal code, and country fields fill in automatically, saving time and reducing errors.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Inside an account:</span> Use the tabs to view all related
          Contacts, Opportunities, the Activity Timeline, and Related Emails. You can create new
          contacts directly from the Contacts tab — the account and owner are filled in for you
          automatically.
        </p>
        <ProTip>
          <span className="font-medium">Account strategy:</span> Use the completeness indicator on
          each account to spot gaps in your data. Complete profiles help your team provide better
          service and make stronger pitches.
        </ProTip>
      </section>
      <Separator />

      <section id="contacts" aria-labelledby="contacts-heading">
        <div className="flex items-center gap-2 mb-2">
          <UserCircle className="h-4 w-4" aria-hidden="true" />
          <h3 id="contacts-heading" className="font-semibold">
            Contacts
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Contacts are the people at your accounts — the decision-makers, champions, and
          stakeholders you interact with. Each contact is linked to a parent account so you always
          know who belongs where.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">What you can track:</span> Name, title, department, email,
          phone, mailing address, and communication preferences.
        </p>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">Inside a contact:</span> View the activity timeline, related
          emails, notes, and tasks. You can also see all opportunities where this contact plays a
          role.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Click-to-edit:</span> On the contact detail page, click a
          field value to edit it inline and save without opening a separate form. The same
          click-to-edit convenience is available on several detail pages, including Tickets, Quotes,
          and RFPs.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Deleting a contact (admins):</span> Admins can delete a
          contact from its detail page using the Delete button. A confirmation warning explains what
          happens first — related records keep their history, but the contact no longer appears
          anywhere in the CRM. Multiple contacts can be deleted at once from the list page using
          bulk actions.
        </p>
      </section>
      <Separator />

      <section id="leads" aria-labelledby="leads-heading">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-4 w-4" aria-hidden="true" />
          <h3 id="leads-heading" className="font-semibold">
            Leads
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Leads are potential customers who have not yet been qualified. They might come from web
          forms, trade shows, referrals, or cold outreach. Your job is to research, engage, and
          qualify them — then convert the good ones into accounts, contacts, and opportunities.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">What you can track:</span> Name, company, email, phone,
          address, lead source, status, rating, and any notes from your conversations. Address
          autocomplete is available — start typing and select from Google Maps suggestions to
          auto-fill city, state, postal code, and country.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Converting a lead:</span> When a lead is qualified, click
          the <span className="font-medium">Convert</span> button on the lead detail page. This
          creates an Account, Contact, and Opportunity in one step — carrying forward all the
          information you have already captured.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Notes at creation:</span> The New Lead form includes a
          Description field, so you can capture context the moment you create the lead. The
          Description is also editable on the lead detail page, and it carries over to the contact,
          account, and opportunity when you convert.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          Add notes directly from the lead's Notes tab to record important context from your
          conversations. These notes transfer over when you convert the lead.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Lead Source column:</span> The Leads list shows a sortable
          Lead Source column, so you can group and prioritize leads by where they came from (web
          form, referral, trade show, and so on). Leads captured through web forms have their source
          populated automatically.
        </p>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">Lead Score:</span> Every active lead displays a color-coded
          Score badge (0-100) in the list and on the detail page. The score combines four signals —
          engagement recency, profile completeness, lead source quality, and status timeliness — to
          help you prioritize which leads to work first. See{' '}
          <a
            href="#lead-scoring"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Lead Scoring
          </a>{' '}
          under Analytics for the full scoring breakdown.
        </p>
        <ProTip>
          <span className="font-medium">High-volume lead management:</span> Use the Lead Score and
          status filters to focus on the most promising leads first. Set up follow-up tasks
          immediately after each call so you never forget to circle back.
        </ProTip>
      </section>
      <Separator />

      <section id="opportunities" aria-labelledby="opportunities-heading">
        <div className="flex items-center gap-2 mb-2">
          <DollarSign className="h-4 w-4" aria-hidden="true" />
          <h3 id="opportunities-heading" className="font-semibold">
            Opportunities
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Opportunities are your active deals — the ones with revenue on the line. Each opportunity
          tracks the deal amount, stage, probability, expected close date, and competitive
          information.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Inside an opportunity:</span> Use the tabs to manage Contact
          Roles (who is involved in the deal), Line Items (what you are selling), Activities,
          History, Attachments, and Related Emails. You can create quotes directly from an
          opportunity, including synced (live) quotes that automatically update the opportunity
          amount when line items change. The contact role dropdown is searchable — start typing to
          filter the list.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Billing address:</span> Opportunities include billing
          address fields so you can track where invoices should be sent directly on the deal record,
          before a quote or order is created.
        </p>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">Pipeline stages:</span> Move opportunities through stages as
          the deal progresses — from Prospecting through Negotiation to Closed Won. The stage
          progress bar on each record gives you a visual snapshot of where the deal stands.{' '}
          <span className="font-medium">Note:</span> Only Admin and Super Admin users can move
          opportunities to Closed Won or edit them after closing.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">List or Kanban view:</span> Switch the Opportunities page
          between the standard list and a Kanban board grouped by stage. In Kanban view, drag a deal
          card from one stage column to another to change its stage — a quick way to advance several
          deals while working your pipeline.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Deal Health Score:</span> Every open opportunity displays a
          color-coded Health badge (0-100) in the list and on the detail page. The score combines
          four signals — recent activity, sales process milestones, close date health, and stage
          probability — to flag at-risk deals before they stall. Scores range from Critical (red,
          0-19) to Healthy (green, 80-100). On the detail page, the Deal Health card breaks down
          each signal so you can see exactly what needs attention. See{' '}
          <a
            href="#deal-health"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Deal Health Score
          </a>{' '}
          under Analytics for the full scoring breakdown.
        </p>
        <ProTip>
          <span className="font-medium">Pipeline hygiene:</span> Review your opportunities weekly.
          Update close dates that have slipped, advance deals that have moved forward, and mark
          stale deals as Closed Lost. A clean pipeline gives you (and your manager) an accurate
          forecast.
        </ProTip>
      </section>
      <Separator />

      <section id="duplicate-detection" aria-labelledby="duplicate-detection-heading">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <h3 id="duplicate-detection-heading" className="font-semibold">
            Duplicate Detection
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Nobody wants to call a prospect who already talked to a teammate yesterday. Grackle CRM
          checks for potential duplicates in real time whenever you create a new account, contact,
          or lead:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Accounts</span> — Matched by name similarity, phone
            number, and website domain
          </li>
          <li>
            <span className="font-medium">Contacts</span> — Matched by email address, name
            similarity, and phone number
          </li>
          <li>
            <span className="font-medium">Leads</span> — Matched by email, name + company
            combination, and phone. Also checks for matching contacts across entities
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2 mb-2">
          If a match is found, you will see a warning with a confidence level (
          <span className="font-medium">High</span> at 70%+ or{' '}
          <span className="font-medium">Medium</span> at 40%+). Click a match to open the existing
          record in a new tab. Warnings are advisory — you can dismiss them and create the record if
          it is genuinely new.
        </p>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">Admin feature:</span> The Duplicates page (under CRM in the
          sidebar) lets administrators run batch scans across all records, review detected matches,
          and resolve them.
        </p>
      </section>
      <Separator />

      <section id="record-merging" aria-labelledby="record-merging-heading">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-4 w-4" aria-hidden="true" />
          <h3 id="record-merging-heading" className="font-semibold">
            Record Merging
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Found duplicates? You can merge them into a single clean record. Here is how:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            On any Accounts, Contacts, or Leads list page, select exactly{' '}
            <span className="font-medium">2 records</span> using the checkboxes
          </li>
          <li>
            A <span className="font-medium">Merge</span> button appears in the bulk actions bar —
            click it
          </li>
          <li>
            Choose which record to keep as the <span className="font-medium">master</span>
          </li>
          <li>For each field where the values differ, pick the one you want to keep</li>
          <li>
            Click <span className="font-medium">Merge</span> to combine them
          </li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          All related records (tasks, notes, opportunities, emails) are automatically reassigned to
          the master record. The duplicate is soft-deleted — nothing is permanently lost.
        </p>
      </section>
    </>
  )
}
